
import { Zone, ZoneRect } from '../types';

export const doLineSegmentsIntersect = (
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    p3: { x: number; y: number },
    p4: { x: number; y: number }
  ): boolean => {
    const det = (p2.x - p1.x) * (p4.y - p3.y) - (p4.x - p3.x) * (p2.y - p1.y);
    if (det === 0) return false;
    const lambda = ((p4.y - p3.y) * (p4.x - p1.x) + (p3.x - p4.x) * (p4.y - p1.y)) / det;
    const gamma = ((p1.y - p2.y) * (p4.x - p1.x) + (p2.x - p1.x) * (p4.y - p1.y)) / det;
    return (0 < lambda && lambda < 1) && (0 < gamma && gamma < 1);
  };

export const isPointInRect = (p: {x: number, y: number}, r: ZoneRect) => {
    return p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;
};

export const isPointInZone = (p: {x: number, y: number}, zone: Zone) => {
    return (zone.rects || []).some(r => isPointInRect(p, r));
};

export const doRectsOverlap = (r1: ZoneRect, r2: ZoneRect): boolean => {
    return !(r1.x > r2.x + r2.w || r1.x + r1.w < r2.x || r1.y > r2.y + r2.h || r1.y + r1.h < r2.y);
};

export const doZonesOverlap = (z1: Zone, z2: Zone): boolean => {
    const rects1 = z1.rects || [];
    const rects2 = z2.rects || [];
    for (const r1 of rects1) {
        for (const r2 of rects2) {
            if (doRectsOverlap(r1, r2)) return true;
        }
    }
    return false;
};

export const getZoneCenter = (zone: Zone) => {
    const rects = zone.rects || [];
    if (rects.length === 0) return { x: 0, y: 0 };
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    rects.forEach(r => {
        minX = Math.min(minX, r.x);
        minY = Math.min(minY, r.y);
        maxX = Math.max(maxX, r.x + r.w);
        maxY = Math.max(maxY, r.y + r.h);
    });
    return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
};

export const getPathLengthMeters = (points: {x: number, y: number}[], pixelsPerMeter: number): number => {
    if (points.length < 2 || pixelsPerMeter <= 0) return 0;
    let totalDist = 0;
    for (let i = 0; i < points.length - 1; i++) {
        totalDist += Math.sqrt(Math.pow(points[i+1].x - points[i].x, 2) + Math.pow(points[i+1].y - points[i].y, 2));
    }
    return totalDist / pixelsPerMeter;
};

export const getZoneAreaM2 = (w: number, h: number, pixelsPerMeter: number): number => {
    if (pixelsPerMeter <= 0) return 0;
    return (w / pixelsPerMeter) * (h / pixelsPerMeter);
};

/** Converts overlapping rectangles into an equivalent non-overlapping set. */
export const normalizeZoneRects = (rects: ZoneRect[]): ZoneRect[] => {
  if (rects.length < 2) return [...rects];
  const xs = [...new Set(rects.flatMap(rect => [rect.x, rect.x + rect.w]))].sort((a, b) => a - b);
  const ys = [...new Set(rects.flatMap(rect => [rect.y, rect.y + rect.h]))].sort((a, b) => a - b);
  const cells: ZoneRect[] = [];

  for (let yi = 0; yi < ys.length - 1; yi++) {
    for (let xi = 0; xi < xs.length - 1; xi++) {
      const cell = { x: xs[xi], y: ys[yi], w: xs[xi + 1] - xs[xi], h: ys[yi + 1] - ys[yi] };
      const center = { x: cell.x + cell.w / 2, y: cell.y + cell.h / 2 };
      if (cell.w > 0 && cell.h > 0 && rects.some(rect => isPointInRect(center, rect))) cells.push(cell);
    }
  }

  const horizontal: ZoneRect[] = [];
  cells.sort((a, b) => a.y - b.y || a.x - b.x).forEach(cell => {
    const previous = horizontal[horizontal.length - 1];
    if (previous && previous.y === cell.y && previous.h === cell.h && previous.x + previous.w === cell.x) {
      previous.w += cell.w;
    } else horizontal.push({ ...cell });
  });

  const vertical: ZoneRect[] = [];
  horizontal.sort((a, b) => a.x - b.x || a.w - b.w || a.y - b.y).forEach(rect => {
    const previous = vertical[vertical.length - 1];
    if (previous && previous.x === rect.x && previous.w === rect.w && previous.y + previous.h === rect.y) {
      previous.h += rect.h;
    } else vertical.push({ ...rect });
  });
  return vertical;
};

export const getZoneRectsAreaM2 = (rects: ZoneRect[], pixelsPerMeter: number): number =>
  normalizeZoneRects(rects).reduce((total, rect) => total + getZoneAreaM2(rect.w, rect.h, pixelsPerMeter), 0);

/**
 * Sulieja persidengiančias to paties tipo zonas į sudėtines netaisyklingas formas.
 */
export const mergeOverlappingZones = (zones: Zone[], preferredId?: string | null): Zone[] => {
  let merged = [...zones];
  let changed = true;
  
  while (changed) {
    changed = false;
    for (let i = 0; i < merged.length; i++) {
      for (let j = i + 1; j < merged.length; j++) {
        if (merged[i].type === merged[j].type && doZonesOverlap(merged[i], merged[j])) {
          const first = merged[i];
          const second = merged[j];
          const base = second.id === preferredId ? second : first;
          const newZone: Zone = {
            ...base,
            rects: normalizeZoneRects([...(first.rects || []), ...(second.rects || [])]),
            entryPointIds: [...new Set([...(first.entryPointIds || []), ...(second.entryPointIds || [])])]
          };
          merged.splice(j, 1);
          merged.splice(i, 1);
          merged.push(newZone);
          changed = true;
          break;
        }
      }
      if (changed) break;
    }
  }
  return merged;
};
